import React from 'react';
import { Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './LandingPageMenu.scss';
import TagManager from 'react-gtm-module';
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import FacebookIcon from '../common/SocialIcons/FacebookIcon';
import InstagramIcon from '../common/SocialIcons/InstagramIcon';
import LinkedInIcon from '../common/SocialIcons/LinkedInIcon';
import { SOCIAL_LINKS } from '../../config/socialLinks';

type LandingPageMenuProps = {
  isMainMenuPage?: boolean;
  isLoggedIn?: boolean;
  userEmail?: string;
  setShowFeedbackModal: (show: boolean) => void;
  showFeedbackModal: boolean;
};

const LandingPageMenu = ({ isMainMenuPage, isLoggedIn = false, setShowFeedbackModal, showFeedbackModal, userEmail }: LandingPageMenuProps) => {
  const { i18n } = useTranslation('mainMenu');
  const languageIsEnglish = i18n.language.startsWith('en');

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  return (
    <>
      <div className="landing-page-menu-wrapper">
        <div className="landing-page-menu-flex-column">
          <Nav.Link
            className="menuElement"
            as={Link}
            to="/faq"
            onClick={() => {
              TagManager.dataLayer({
                dataLayer: {
                  event: 'landing_page_what_is_jaakko_faq_clicked',
                  category: 'User Interaction',
                  action: 'Click',
                  label: 'Landing Page What is Jaakko.com FAQ Clicked',
                },
              });
            }}
          >
            <Trans i18nKey="mainMenu.whatIsJaakko">
              What is Jaakko.com?
            </Trans>
          </Nav.Link>
          <Nav.Link
            className="menuElement"
            onClick={() => {
              TagManager.dataLayer({
                dataLayer: {
                  event: 'landing_page_feedback_clicked',
                  category: 'User Interaction',
                  action: 'Click',
                  label: 'Landing Page Feedback Clicked',
                },
              });
              setShowFeedbackModal(true);
            }}
          >
            <Trans i18nKey="mainMenu.feedback">Feedback</Trans>
          </Nav.Link>
          <Nav.Link
            className="menuElement"
            as={Link}
            to="/contact"
            onClick={() => {
              TagManager.dataLayer({
                dataLayer: {
                  event: 'landing_page_contact_clicked',
                  category: 'User Interaction',
                  action: 'Click',
                  label: 'Landing Page Contact Clicked',
                },
              });
            }}
          >
            <Trans i18nKey="mainMenu.contact">Contact</Trans>
          </Nav.Link>
          <Nav.Link
            className="menuElement"
            href={languageIsEnglish ? "https://info.jaakko.com/en-gb/terms_and_conditions" : "https://info.jaakko.com/kayttoehdot"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              TagManager.dataLayer({
                dataLayer: {
                  event: 'landing_page_terms_and_conditions_clicked',
                  category: 'User Interaction',
                  action: 'Click',
                  label: 'Landing Page Terms and Conditions Clicked',
                },
              });
            }}
          >
            <Trans i18nKey="mainMenu.termsConditions">Terms & Conditions</Trans>
          </Nav.Link>
          <Nav.Link
            className="menuElement"
            href={languageIsEnglish ? "https://info.jaakko.com/en-gb/privacy_policy" : "https://info.jaakko.com/tietosuoja"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              TagManager.dataLayer({
                dataLayer: {
                  event: 'landing_page_privacy_policy_clicked',
                  category: 'User Interaction',
                  action: 'Click',
                  label: 'Landing Page Privacy Policy Clicked',
                },
              });
            }}
          >
            <Trans i18nKey="mainMenu.privacyPolicy">Privacy Policy</Trans>
          </Nav.Link>
          <Nav.Link
            className="menuElement"
            href={languageIsEnglish ? "https://info.jaakko.com/en-gb/cookies" : "https://info.jaakko.com/evasteet"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              TagManager.dataLayer({
                dataLayer: {
                  event: 'landing_page_cookies_clicked',
                  category: 'User Interaction',
                  action: 'Click',
                  label: 'Landing Page Cookies Clicked',
                },
              });
            }}
          >
            <Trans i18nKey="mainMenu.cookies">Cookies</Trans>
          </Nav.Link>

          <div className="menuElement landing-page-social-links">
            <a
              className={`landing-page-social-links__icon ${!SOCIAL_LINKS.facebook ? 'landing-page-social-links__icon--disabled' : ''}`}
              href={SOCIAL_LINKS.facebook || undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              onClick={() => {
                TagManager.dataLayer({
                  dataLayer: {
                    event: 'landing_page_social_facebook_clicked',
                    category: 'User Interaction',
                    action: 'Click',
                    label: 'Landing Page Facebook Clicked',
                  },
                });
              }}
            >
              <FacebookIcon size={18} />
            </a>
            <a
              className={`landing-page-social-links__icon ${!SOCIAL_LINKS.linkedin ? 'landing-page-social-links__icon--disabled' : ''}`}
              href={SOCIAL_LINKS.linkedin || undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              onClick={() => {
                TagManager.dataLayer({
                  dataLayer: {
                    event: 'landing_page_social_linkedin_clicked',
                    category: 'User Interaction',
                    action: 'Click',
                    label: 'Landing Page LinkedIn Clicked',
                  },
                });
              }}
            >
              <LinkedInIcon size={18} />
            </a>
            <a
              className={`landing-page-social-links__icon ${!SOCIAL_LINKS.instagram ? 'landing-page-social-links__icon--disabled' : ''}`}
              href={SOCIAL_LINKS.instagram || undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              onClick={() => {
                TagManager.dataLayer({
                  dataLayer: {
                    event: 'landing_page_social_instagram_clicked',
                    category: 'User Interaction',
                    action: 'Click',
                    label: 'Landing Page Instagram Clicked',
                  },
                });
              }}
            >
              <InstagramIcon size={18} />
            </a>
          </div>

          <div className="menuElement landing-page-language-selector">
            <div className="landing-page-language-selector__title">
              <Trans i18nKey="mainMenu.language">Language</Trans>
            </div>
            <div className="landing-page-language-selector__options">
              <button
                className={`landing-page-language-selector__option ${languageIsEnglish ? 'landing-page-language-selector__option--active' : ''}`}
                onClick={() => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: 'landing_page_language_selector_en_clicked',
                      category: 'User Interaction',
                      action: 'Click',
                      label: 'Landing Page Language Selector EN Clicked',
                    },
                  });
                  changeLanguage('en');
                }}
              >
                EN
              </button>
              <button
                className={`landing-page-language-selector__option ${!languageIsEnglish ? 'landing-page-language-selector__option--active' : ''}`}
                onClick={() => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: 'landing_page_language_selector_fi_clicked',
                      category: 'User Interaction',
                      action: 'Click',
                      label: 'Landing Page Language Selector FI Clicked',
                    },
                  });
                  changeLanguage('fi');
                }}
              >
                FI
              </button>
            </div>
          </div>
        </div>
      </div>

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        isLoggedIn={isLoggedIn}
        userEmail={userEmail}
      />
    </>
  );
};

export default LandingPageMenu;
